import type { SupabaseClient } from '@supabase/supabase-js';
import type { RegisterStore, RosterStore, TimesheetStore, TrustedPersonStore } from '../ports.js';
import type {
  Employee,
  TimeEntry,
  TimeEntrySource,
  TrustedEntry,
  TrustedPersonRecord,
} from '../domain.js';
import { rowToEmployee, rowToTimeEntry } from '../rows.js';
import { rowToAdminEmployee, type AdminEmployee, type AdminEmployeeRaw } from '../register.js';

/**
 * `RosterStore` and `TimesheetStore` over PostgREST, using the BROWSER's authed
 * client under RLS — the `SupabaseBoardStore` model, not a service-role adapter.
 * The caller's own policies apply exactly as they did when these queries were
 * inline in the components; this is a relocation of the read path, not a change
 * of privilege.
 *
 * They share a file because they share that constraint and nothing else; the
 * ports stay separate because the aggregates are.
 */

/**
 * EXPORTED and pinned by `rosterStore.test.ts`, like the board's lists.
 *
 * This one is permission-sensitive rather than merely tidy: `staff_employees`
 * carries COLUMN grants (migration 0016), so `select('*')` is a permission
 * ERROR for a staff-client read, and a column added here that the staff role
 * cannot see breaks the staff app while leaving admin working. It was
 * copy-pasted identically into five views before this.
 */
export const EMPLOYEE_COLUMNS = 'id,employee_id,last_name,first_name,phone,team,is_senior,nickname';
export const TIME_ENTRY_COLUMNS = 'id,employee_id,clock_in,clock_out,source,updated_by,updated_at';

function unwrap<T>(r: { data: T[] | null; error: { message: string } | null }, what: string): T[] {
  if (r.error) throw new Error(`${what}: ${r.error.message}`);
  return r.data ?? [];
}

export class SupabaseRosterStore implements RosterStore {
  constructor(private readonly db: SupabaseClient) {}

  async current(): Promise<Employee[]> {
    const r = await this.db.from('staff_current_employees').select(EMPLOYEE_COLUMNS).order('first_name');
    return unwrap(r, 'staff_current_employees').map((row) => rowToEmployee(row as never));
  }

  async all(): Promise<Employee[]> {
    const r = await this.db
      .from('staff_employees')
      .select(EMPLOYEE_COLUMNS)
      .eq('kind', 'employee') // ADR-0024: « Hors Staff » people are not roster
      .order('first_name');
    return unwrap(r, 'staff_employees').map((row) => rowToEmployee(row as never));
  }

  async myEmployeeId(): Promise<string | null> {
    const { data, error } = await this.db.rpc('staff_employee_id');
    if (error) throw new Error(`staff_employee_id: ${error.message}`);
    // The RPC returns null for an account with no linked register row — a
    // normal state (a new sign-in before the operator links it), not an error.
    return (data as string | null) ?? null;
  }
}

/**
 * « Hors Staff » reads (ADR-0024). Every call is an RPC or an RLS-gated single-row
 * table: RLS hides trusted punches from direct reads of `staff_time_entries`, so
 * the RPC is the only way in — which is the point.
 */
export class SupabaseTrustedPersonStore implements TrustedPersonStore {
  constructor(private readonly db: SupabaseClient) {}

  async list(): Promise<TrustedPersonRecord[]> {
    const { data, error } = await this.db.rpc('staff_admin_list_trusted');
    if (error) throw new Error(`staff_admin_list_trusted: ${error.message}`);
    return ((data ?? []) as TrustedPersonRow[]).map((r) => ({
      id: r.id,
      firstName: r.first_name,
      lastName: r.last_name,
      nickname: r.nickname ?? null,
      hasPin: r.has_pin,
      archivedAt: r.archived_at,
    }));
  }

  async entriesBetween(fromIso: string, toIso: string): Promise<TrustedEntry[]> {
    const { data, error } = await this.db.rpc('staff_trusted_entries', { p_from: fromIso, p_to: toIso });
    if (error) throw new Error(`staff_trusted_entries: ${error.message}`);
    return ((data ?? []) as TrustedEntryRow[]).map((r) => ({
      id: r.id,
      personId: r.employee_id,
      clockIn: r.clock_in,
      clockOut: r.clock_out,
      source: r.source,
    }));
  }

  async retentionDays(): Promise<number> {
    const { data, error } = await this.db
      .from('staff_presence_policy')
      .select('trusted_retention_days')
      .eq('singleton', true)
      .maybeSingle();
    if (error) throw new Error(`staff_presence_policy: ${error.message}`);
    // The table's default, if the row is somehow not readable — never a made-up figure.
    return (data as { trusted_retention_days: number } | null)?.trusted_retention_days ?? 120;
  }
}

interface TrustedPersonRow {
  id: string;
  first_name: string;
  last_name: string;
  nickname: string | null;
  has_pin: boolean;
  archived_at: string | null;
}

interface TrustedEntryRow {
  id: number;
  employee_id: string;
  clock_in: string;
  clock_out: string | null;
  source: TimeEntrySource;
}

export class SupabaseTimesheetStore implements TimesheetStore {
  constructor(private readonly db: SupabaseClient) {}

  async entriesBetween(fromIso: string, toIso: string, employeeId?: string): Promise<TimeEntry[]> {
    // Half-open — `>= from`, `< to` — matching every call site this replaced.
    // `clock_in` is timestamptz, so the bounds are instants, never bare dates.
    let q = this.db
      .from('staff_time_entries')
      .select(TIME_ENTRY_COLUMNS)
      .gte('clock_in', fromIso)
      .lt('clock_in', toIso);
    if (employeeId !== undefined) q = q.eq('employee_id', employeeId);
    const r = await q.order('clock_in');
    return unwrap(r, 'staff_time_entries').map((row) => rowToTimeEntry(row as never));
  }
}

/**
 * `RegisterStore` over the `staff_admin_list_employees` RPC (0023/0024).
 *
 * The RPC is `SECURITY DEFINER` and `is_admin`-gated, so authorization lives in
 * the database, not here — a non-admin session gets an error, not a filtered
 * list. That is the point: this adapter carries no privilege of its own.
 */
export class SupabaseRegisterStore implements RegisterStore {
  constructor(private readonly db: SupabaseClient) {}

  async listEmployees(): Promise<AdminEmployee[]> {
    const { data, error } = await this.db.rpc('staff_admin_list_employees');
    if (error) throw new Error(`staff_admin_list_employees: ${error.message}`);
    return ((data ?? []) as AdminEmployeeRaw[]).map(rowToAdminEmployee);
  }
}
